import { useTranslation } from "react-i18next";
import { useDetectLanguage } from "../helpers/language-detector";
import { useState, useEffect, useRef, useLayoutEffect } from "react";

const SLIDE_AMOUNT = 5;
const AUTO_SLIDE_DELAY = 5000;

export default function Carousel() {
    const { t } = useTranslation();
    const lang = useDetectLanguage();

    const [news, setNews] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);

    const trackRef = useRef(null);
    const intervalRef = useRef(null);
    const slideWidthRef = useRef(0);
    const visibleIndexRef = useRef(1);
    const isSlidingRef = useRef(false);
    const fetchIdRef = useRef(0);

    useEffect(() => {
        clearInterval(intervalRef.current);
        intervalRef.current = null;

        isSlidingRef.current = false;
        visibleIndexRef.current = 1;

        if (trackRef.current) {
            trackRef.current.style.transition = "none";
            trackRef.current.style.transform = "translateX(0)";
        }

        setNews([]);
        setCurrentIndex(0);
    }, [lang]);

    useEffect(() => {
        const controller = new AbortController();
        const fetchId = ++fetchIdRef.current;

        fetch(`/api/${lang}/news`, { signal: controller.signal })
            .then(res => res.json())
            .then(data => {
                if (fetchId !== fetchIdRef.current) return;
                setNews(data.slice(0, SLIDE_AMOUNT));
            })
            .catch(err => {
                if (err.name !== "AbortError") console.error(err);
            });

        return () => controller.abort();
    }, [lang]);

    const slides =
        news.length > 0
            ? [news[news.length - 1], ...news, news[0]]
            : [];

    useLayoutEffect(() => {
        if (!trackRef.current || slides.length === 0) return;

        visibleIndexRef.current = 1;
        isSlidingRef.current = false;

        const width = trackRef.current.children[0].getBoundingClientRect().width;
        slideWidthRef.current = width;

        trackRef.current.style.transition = "none";
        trackRef.current.style.transform = `translateX(-${width}px)`;

        setCurrentIndex(0);

        clearInterval(intervalRef.current);
        intervalRef.current = setInterval(nextSlide, AUTO_SLIDE_DELAY);
    }, [slides.length]);

    const handleTransitionEnd = () => {
        if (!isSlidingRef.current || !trackRef.current) return;

        let newIndex = visibleIndexRef.current;

        if (visibleIndexRef.current === slides.length - 1) {
            newIndex = 1;
        } else if (visibleIndexRef.current === 0) {
            newIndex = slides.length - 2;
        } else {
            isSlidingRef.current = false;
            return;
        }

        const track = trackRef.current;
        track.style.transition = "none";
        track.style.transform = `translateX(-${slideWidthRef.current * newIndex}px)`;
        track.getBoundingClientRect();
        track.style.transition = "transform 0.75s ease-in-out";

        visibleIndexRef.current = newIndex;
        setCurrentIndex(newIndex - 1);
        isSlidingRef.current = false;
    };

    const nextSlide = () => {
        if (isSlidingRef.current || news.length === 0) return;

        isSlidingRef.current = true;
        visibleIndexRef.current += 1;

        trackRef.current.style.transition = "transform 0.75s ease-in-out";
        trackRef.current.style.transform =
            `translateX(-${slideWidthRef.current * visibleIndexRef.current}px)`;

        setCurrentIndex((visibleIndexRef.current - 1 + news.length) % news.length);
    };

    const prevSlide = () => {
        if (isSlidingRef.current || news.length === 0) return;

        isSlidingRef.current = true;
        visibleIndexRef.current -= 1;

        trackRef.current.style.transition = "transform 0.75s ease-in-out";
        trackRef.current.style.transform =
            `translateX(-${slideWidthRef.current * visibleIndexRef.current}px)`;

        setCurrentIndex(
            (visibleIndexRef.current - 1 + news.length) % news.length
        );
    };

    const goToSlide = index => {
        if (isSlidingRef.current || news.length === 0) return;

        clearInterval(intervalRef.current);

        isSlidingRef.current = true;
        visibleIndexRef.current = index + 1;

        trackRef.current.style.transition = "transform 0.75s ease-in-out";
        trackRef.current.style.transform =
            `translateX(-${slideWidthRef.current * visibleIndexRef.current}px)`;

        setCurrentIndex(index);

        intervalRef.current = setInterval(nextSlide, AUTO_SLIDE_DELAY);
    };

    useEffect(() => {
        const onResize = () => {
            if (!trackRef.current || slides.length === 0) return;

            const width = trackRef.current.children[0].getBoundingClientRect().width;
            slideWidthRef.current = width;

            trackRef.current.style.transition = "none";
            trackRef.current.style.transform =
                `translateX(-${width * visibleIndexRef.current}px)`;
        };

        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, [slides.length]);

    return (
        <div className="carousel">
            <div
                className="track"
                ref={trackRef}
                onTransitionEnd={handleTransitionEnd}
            >
                {slides.map((item, index) => (
                    <div className="slide" key={`${item.id}-${index}`}>
                        <div className="thumbnail">
                            <img src={item.thumbnail} alt={item.title} />
                        </div>
                        <div className="details">
                            <div className="info">
                                <h2>{item.title}</h2>
                                <p><span lang="en">{item.published_date}</span></p>
                            </div>
                            <a
                                href={`/news/${item.id}?lang=${lang}`}
                                className="read-more-btn"
                            >
                                {t("read-more")}
                            </a>
                        </div>
                    </div>
                ))}
            </div>

            <div className="carousel-controls">
                <button className="ctrl-btn prev" onClick={prevSlide}>
                    <i className="bi bi-caret-left-fill" />
                </button>
                <button className="ctrl-btn next" onClick={nextSlide}>
                    <i className="bi bi-caret-right-fill" />
                </button>
            </div>

            <div className="indicator-container">
                {news.map((_, index) => (
                    <span
                        key={index}
                        className={`indicator ${index === currentIndex ? "active" : ""}`}
                        onClick={() => goToSlide(index)}
                    />
                ))}
            </div>
        </div>
    );
}
